/**
 * Compact Clause picker for Word Add-in (320px task pane).
 * Displays approved clauses in a searchable list.
 * Used in CommentCompose toolbar.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpen, Search, X, FileInput } from 'lucide-react'
import { useClauseList } from '../../hooks/useClauses'
import type { Clause, ClauseCategory } from '../../services/clausesApi'

const CATEGORY_STYLES: Record<string, string> = {
  LIABILITY: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  FORCE_MAJEURE: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  TERMINATION: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  CONFIDENTIALITY: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  GDPR: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  IP: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  PAYMENT: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  DISPUTE: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
}

/** Map UPPER_SNAKE category to camelCase i18n key suffix */
function categoryKey(cat: string): string {
  return cat.toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

interface Props {
  onSelect: (clause: Clause) => void
  onClose: () => void
  /** Insert clause directly into Word document as Content Control */
  onInsertToDocument?: (clause: Clause) => void
}

export function ClausePicker({ onSelect, onClose, onInsertToDocument }: Props) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<ClauseCategory | ''>('')

  const { data: clauses = [], isLoading, isError } = useClauseList({
    search: search || undefined,
    category: category || undefined,
    status: 'APPROVED',
  })

  // Hide picker entirely on error (e.g. 403 upgrade_required for non-Business tiers)
  if (isError) return null

  return (
    <div className="absolute bottom-full left-0 mb-1 w-[280px] max-h-64 bg-white dark:bg-neutral-900 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-700 z-20 flex flex-col text-xs">
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-neutral-200 dark:border-neutral-700">
        <span className="flex items-center gap-1 font-medium text-neutral-700 dark:text-neutral-300">
          <BookOpen className="w-3.5 h-3.5" />
          {t('negotiate.clause.suggest', 'Suggest Clause')}
        </span>
        <button onClick={onClose} className="p-0.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800">
          <X className="w-3.5 h-3.5 text-neutral-400" />
        </button>
      </div>

      {/* Search */}
      <div className="px-2 py-1.5 border-b border-neutral-100 dark:border-neutral-800">
        <div className="relative">
          <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('negotiate.clause.search', 'Search...')}
            className="w-full pl-5 pr-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary-500"
            autoFocus
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ClauseCategory | '')}
          className="mt-1 w-full px-1.5 py-0.5 rounded border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-[11px]"
        >
          <option value="">{t('negotiate.clause.allCategories', 'All categories')}</option>
          <option value="LIABILITY">{t('negotiate.clause.liability', 'Liability')}</option>
          <option value="FORCE_MAJEURE">{t('negotiate.clause.forceMajeure', 'Force majeure')}</option>
          <option value="TERMINATION">{t('negotiate.clause.termination', 'Termination')}</option>
          <option value="CONFIDENTIALITY">{t('negotiate.clause.confidentiality', 'Confidentiality')}</option>
          <option value="GDPR">{t('negotiate.clause.gdpr', 'GDPR')}</option>
          <option value="IP">{t('negotiate.clause.ip', 'IP')}</option>
          <option value="PAYMENT">{t('negotiate.clause.payment', 'Payment')}</option>
          <option value="DISPUTE">{t('negotiate.clause.dispute', 'Dispute')}</option>
        </select>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="py-3 text-center text-neutral-400">{t('common.loading', 'Loading...')}</div>
        ) : clauses.length === 0 ? (
          <div className="py-3 text-center text-neutral-400">
            {t('negotiate.clause.noResults', 'No clauses found.')}
          </div>
        ) : (
          clauses.map((clause) => (
            <div
              key={clause.id}
              className="flex items-start gap-1 px-2 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
            >
              <button
                onClick={() => onSelect(clause)}
                className="flex-1 text-left min-w-0"
              >
                <div className="flex items-center gap-1">
                  <span className="font-medium text-neutral-800 dark:text-neutral-200 truncate">
                    {clause.title}
                  </span>
                  <span className={`shrink-0 px-1 py-0.5 text-[8px] font-medium rounded ${CATEGORY_STYLES[clause.category] ?? 'bg-neutral-100 text-neutral-500'}`}>
                    {t(`negotiate.clause.${categoryKey(clause.category)}`, clause.category)}
                  </span>
                  <span className="shrink-0 text-[8px] text-neutral-400 dark:text-neutral-500">
                    v{clause.version}
                  </span>
                </div>
                <div className="text-[10px] text-neutral-500 dark:text-neutral-400 line-clamp-2 mt-0.5">
                  {clause.body}
                </div>
              </button>
              {onInsertToDocument && (
                <button
                  onClick={(e) => { e.stopPropagation(); onInsertToDocument(clause) }}
                  className="shrink-0 mt-0.5 p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 transition-colors"
                  title={t('negotiate.clauseInsert.insertToDocument', 'Insert into document')}
                >
                  <FileInput size={12} />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
